import { useAppStore } from '../store/useAppStore'
import { ChevronRight, User } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '../lib/api'
import { useState, useEffect } from 'react'

interface LotteryLatest {
  draw_date: string
  first_prize: number | null
  two_digit: number | null
  three_digit_front: number[] | null
  three_digit_last: number[] | null
}

const THAI_MONTHS_FULL = ['มกราคม','กุมภาพันธ์','มีนาคม','เมษายน','พฤษภาคม','มิถุนายน','กรกฎาคม','สิงหาคม','กันยายน','ตุลาคม','พฤศจิกายน','ธันวาคม']

function formatThaiDateFull(dateStr: string): string {
  const [y, m, d] = dateStr.split('-').map(Number)
  return `${d} ${THAI_MONTHS_FULL[m - 1]} ${y + 543}`
}

/** Featured Government Lottery Card — luxury sage+gold design */
function FeaturedLotteryCard() {
  const { data, isLoading } = useQuery({
    queryKey: ['lottery', 'home', 'government'],
    queryFn: () => api.lottery.latest('government') as Promise<LotteryLatest>,
    staleTime: 1000 * 60 * 10,
  })

  if (isLoading) {
    return (
      <div className="card-amulet p-6 mx-4 mb-4 animate-pulse">
        <div className="h-4 w-2/3 mb-2 rounded" style={{ background: 'var(--color-surface-2)' }} />
        <div className="h-12 mb-4 rounded" style={{ background: 'var(--color-surface-2)' }} />
        <div className="grid grid-cols-3 gap-2">
          <div className="h-16 rounded" style={{ background: 'var(--color-surface-2)' }} />
          <div className="h-16 rounded" style={{ background: 'var(--color-surface-2)' }} />
          <div className="h-16 rounded" style={{ background: 'var(--color-surface-2)' }} />
        </div>
      </div>
    )
  }

  if (!data) return null

  const front3 = data.three_digit_front ?? []
  const last3 = data.three_digit_last ?? []

  return (
    <div className="card-premium mx-4 mb-4">
      {/* Header strip */}
      <div className="px-4 py-2.5 rounded-t-[calc(1.5rem-2px)]"
           style={{ background: 'var(--gradient-gold)' }}>
        <p className="text-center text-white text-xs font-semibold tracking-wide">
          ผลสลากกินแบ่งรัฐบาล
        </p>
        <p className="text-center text-white text-[10px] opacity-90 mt-0.5">
          งวดวันที่ {formatThaiDateFull(data.draw_date)}
        </p>
      </div>

      {/* รางวัลที่ 1 — fit container */}
      <div className="px-4 pt-5 pb-4 text-center">
        <div className="text-[10px] uppercase tracking-widest mb-1.5"
             style={{ color: 'var(--color-gold-dark)' }}>
          รางวัลที่ 1
        </div>
        <div className="font-black font-mono"
             style={{
               color: 'var(--color-text)',
               fontSize: 'clamp(2rem, 11vw, 3rem)',
               letterSpacing: '0.05em',
             }}>
          {data.first_prize?.toString().padStart(6, '0') ?? '------'}
        </div>
      </div>

      {/* เลขท้าย 2 ตัว — hero card center */}
      <div className="mx-4 mb-3 py-4 px-4 rounded-2xl text-center"
           style={{
             background: 'var(--gradient-sage)',
             boxShadow: '0 4px 14px rgba(122, 138, 106, 0.25)',
           }}>
        <div className="text-[10px] uppercase tracking-widest mb-2 text-white/85">
          เลขท้าย 2 ตัว
        </div>
        <div className="font-black font-mono text-white"
             style={{
               fontSize: 'clamp(2.5rem, 13vw, 3.5rem)',
               letterSpacing: '0.15em',
               lineHeight: 1.1,
               paddingLeft: '0.15em',
             }}>
          {data.two_digit !== null && data.two_digit !== undefined
            ? String(data.two_digit).padStart(2, '0')
            : '--'}
        </div>
      </div>

      {/* เลขหน้า 3 + ท้าย 3 — secondary row */}
      <div className="grid grid-cols-2 gap-2 px-4 pb-4">
        <PrizeBox
          label="เลขหน้า 3 ตัว"
          values={front3.map(n => n.toString().padStart(3, '0'))}
        />
        <PrizeBox
          label="เลขท้าย 3 ตัว"
          values={last3.map(n => n.toString().padStart(3, '0'))}
        />
      </div>
    </div>
  )
}

function PrizeBox({ label, values }: {
  label: string
  values: string[]
}) {
  return (
    <div className="rounded-xl p-3 text-center"
         style={{
           background: 'var(--color-surface-2)',
           border: '1px solid var(--color-surface-3)',
         }}>
      <div className="text-[10px] font-semibold mb-1.5"
           style={{ color: 'var(--color-text-muted)' }}>
        {label}
      </div>
      <div className="flex items-center justify-center gap-2">
        {values.length > 0 ? values.map((v, i) => (
          <div key={i} className="font-mono font-bold text-xl"
               style={{ color: 'var(--color-text)' }}>
            {v}
          </div>
        )) : (
          <div className="font-mono font-bold text-xl"
               style={{ color: 'var(--color-text-soft)' }}>
            —
          </div>
        )}
      </div>
    </div>
  )
}

export default function HomePage() {
  const { profile, lineUid, setProfile } = useAppStore()
  const navigate = useNavigate()

  const tickets = profile?.dream_quota?.paid_tickets ?? 0
  const usedToday = profile?.dream_quota?.used_today ?? false

  const [checkedIn, setCheckedIn] = useState(profile?.checkin?.checked_in_today ?? false)
  const [checkingIn, setCheckingIn] = useState(false)

  useEffect(() => {
    setCheckedIn(profile?.checkin?.checked_in_today ?? false)
  }, [profile?.checkin?.checked_in_today])

  async function handleCheckin() {
    if (checkedIn || checkingIn || !lineUid) return
    setCheckingIn(true)
    setCheckedIn(true)
    try {
      await api.user.checkin(lineUid)
      const fresh = await api.user.profile(lineUid) as Parameters<typeof setProfile>[0]
      setProfile(fresh)
    } catch {
      setCheckedIn(false)
    } finally {
      setCheckingIn(false)
    }
  }

  const dreamBadge = (() => {
    if (tickets > 0) return `🎫 ${tickets} ครั้ง`
    if (!usedToday) return 'ฟรี 1 ครั้ง/วัน'
    return 'ใช้แล้ววันนี้'
  })()

  const cards = [
    {
      icon: '🔮',
      title: 'ทำนายฝัน',
      desc: 'วิเคราะห์ตามตำราโบราณ → เลขมงคล',
      path: '/dream',
      badge: dreamBadge,
      featured: true,
    },
    {
      icon: '🎰',
      title: 'ผลหวยย้อนหลัง',
      desc: 'ผลสลากกินแบ่งรัฐบาล 33 งวด',
      path: '/lottery',
      badge: 'อัปเดตทุกงวด',
    },
    {
      icon: '📊',
      title: 'เลขเด่นตามสถิติ',
      desc: 'เลขร้อน-เลขเย็น ย้อนหลัง 10 ปี',
      path: '/heatmap',
      badge: 'ข้อมูล 10 ปี',
    },
  ]

  return (
    <div className="flex flex-col min-h-dvh"
         style={{ background: 'var(--color-bg)' }}>

      {/* ── Header with Logo + Profile ───────────── */}
      <div className="px-4 pt-6 pb-3 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <img
            src="/logo.png"
            alt="Amulet Digit"
            className="w-11 h-11 object-cover rounded-full"
            style={{ border: '1.5px solid var(--color-gold)', boxShadow: '0 2px 8px rgba(184, 148, 90, 0.25)' }}
            onError={(e) => {
              const target = e.target as HTMLImageElement
              target.style.display = 'none'
              target.nextElementSibling?.removeAttribute('style')
            }}
          />
          <span className="text-3xl" style={{ display: 'none' }}>🪬</span>
          <div>
            <div className="text-lg font-bold leading-tight"
                 style={{ color: 'var(--color-gold-dark)' }}>
              Amulet Digit
            </div>
            <div className="text-[10px] leading-tight"
                 style={{ color: 'var(--color-text-muted)' }}>
              ศูนย์รวมความมงคล
            </div>
            {profile?.display_name && (
              <div className="text-[10px] mt-0.5" style={{ color: 'var(--color-text-soft)' }}>
                สวัสดี, {profile.display_name}
              </div>
            )}
          </div>
        </div>
        <button onClick={() => navigate('/profile')}
                className="w-10 h-10 rounded-full flex items-center justify-center overflow-hidden"
                style={{ background: 'var(--color-surface)', border: '1px solid var(--color-surface-3)' }}>
          {profile?.avatar_url ? (
            <img src={profile.avatar_url} alt="" className="w-full h-full object-cover" />
          ) : (
            <User size={18} style={{ color: 'var(--color-text-muted)' }} />
          )}
        </button>
      </div>

      {/* ── Featured Lottery Card ────────────────── */}
      <FeaturedLotteryCard />

      {/* ── ตรวจลอตเตอรี่ CTA ───────────────────── */}
      <div className="px-4 mb-3">
        <button
          onClick={() => navigate('/check')}
          className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl text-left transition-transform active:scale-[0.98] card-amulet"
          style={{ borderColor: 'var(--color-gold)', border: '1px solid var(--color-gold)' }}
        >
          <span className="text-2xl">🎫</span>
          <div className="flex-1">
            <div className="text-sm font-bold" style={{ color: 'var(--color-text)' }}>ตรวจลอตเตอรี่</div>
            <div className="text-xs" style={{ color: 'var(--color-text-muted)' }}>กรอกเลข 6 หลัก ตรวจทุกรางวัลทันที</div>
          </div>
          <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full"
                style={{ background: 'rgba(184,148,90,0.12)', color: 'var(--color-gold-dark)' }}>
            ฟรี ทุกรางวัล
          </span>
        </button>
      </div>

      {/* ── Tagline ──────────────────────────────── */}
      <div className="mx-4 mb-4 py-3 px-4 rounded-2xl text-center"
           style={{ background: 'var(--gradient-gold)', boxShadow: '0 4px 16px rgba(184, 148, 90, 0.3)' }}>
        <div className="text-sm font-semibold text-white tracking-wide">
          ✨ ทำนายฝัน ค้นพบเลขมงคลของคุณ ✨
        </div>
      </div>

      {/* ── Daily Check-in ───────────────────────── */}
      <div className="px-4 mb-3">
        <button
          onClick={handleCheckin}
          disabled={checkedIn || checkingIn}
          className={`w-full p-4 rounded-2xl flex items-center gap-4 text-left transition-all active:scale-[0.98] ${checkedIn ? '' : 'btn-gold shimmer'}`}
          style={checkedIn ? {
            background: 'var(--color-surface)',
            border: '1px solid var(--color-surface-3)',
          } : {}}
        >
          <div className="text-3xl w-12 h-12 flex items-center justify-center rounded-xl flex-shrink-0"
               style={{ background: checkedIn ? 'var(--color-surface-2)' : 'rgba(255,255,255,0.2)' }}>
            {checkedIn ? '✅' : '🎁'}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-bold text-sm"
                 style={{ color: checkedIn ? 'var(--color-text-muted)' : '#fff' }}>
              {checkedIn ? 'เช็คอินแล้ววันนี้' : 'เช็คอินรับตั๋วฟรี!'}
            </div>
            <div className="text-xs mt-0.5"
                 style={{ color: checkedIn ? 'var(--color-text-soft)' : 'rgba(255,255,255,0.8)' }}>
              {checkedIn ? 'มาใหม่พรุ่งนี้นะ 🌟' : '1 ตั๋วฟรี/วัน • ใช้ทำนายฝันได้เลย'}
            </div>
          </div>
          {!checkedIn && <ChevronRight size={18} style={{ color: 'rgba(255,255,255,0.7)' }} />}
        </button>
      </div>

      {/* ── Feature Cards ────────────────────────── */}
      <div className="px-4 flex flex-col gap-3 pb-4">
        {cards.map(card => (
          <button key={card.path} onClick={() => navigate(card.path)}
                  className={`flex items-center gap-4 p-4 rounded-2xl text-left transition-transform active:scale-[0.98] ${card.featured ? 'card-premium' : 'card-amulet'}`}>
            <div className="text-3xl w-14 h-14 flex items-center justify-center rounded-2xl"
                 style={{
                   background: card.featured ? 'var(--gradient-gold)' : 'var(--color-surface-2)',
                 }}>
              {card.icon}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-base" style={{ color: 'var(--color-text)' }}>
                {card.title}
              </div>
              <div className="text-xs mt-0.5" style={{ color: 'var(--color-text-muted)' }}>
                {card.desc}
              </div>
            </div>
            <div className="flex flex-col items-end gap-1">
              <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap"
                    style={{
                      background: card.featured ? 'rgba(255,255,255,0.2)' : 'var(--color-surface-2)',
                      color: card.featured ? 'var(--color-gold-dark)' : 'var(--color-text-muted)',
                    }}>
                {card.badge}
              </span>
              <ChevronRight size={16} style={{ color: 'var(--color-text-soft)' }} />
            </div>
          </button>
        ))}
      </div>

      <div className="mt-auto pt-6 pb-3 px-4">
        <p className="text-center text-[10px]" style={{ color: 'var(--color-text-soft)' }}>
          เพื่อความบันเทิงและข้อมูลเชิงสถิติเท่านั้น • ไม่ใช่คำแนะนำทางการเงิน
        </p>
      </div>
    </div>
  )
}
